/**
 * Central socket invalidation hook. Subscribes to socket events and invalidates
 * TanStack Query cache. Mount once in App (inside SocketProvider).
 * Pages register their rooms via useRegisterSocketRooms().
 */
import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import { useSocketRooms } from '../contexts/SocketRoomsContext';
export function useSocketInvalidation() {
  const queryClient = useQueryClient();
  const { subscribe, unsubscribe, on, off } = useSocket();
  const { user } = useAuth();
  const { rooms: registeredRooms } = useSocketRooms();
  const registeredRoomsKey = registeredRooms.join(',');

  const invalidate = useCallback(
    (predicate) => {
      queryClient.invalidateQueries(predicate);
    },
    [queryClient]
  );

  useEffect(() => {
    const userId = user?.user_id;
    const isCaller = user?.role_slug === 'caller';

    const allRooms = [
      'settings',
      'callers:global',
      'mp_requests',
      userId ? `notifications:${userId}` : null,
      isCaller && userId ? `caller:${userId}` : null,
      ...registeredRooms,
    ].filter(Boolean);

    allRooms.forEach((room) => subscribe(room));

    const handlers = {
      'notification:new': () => invalidate({ queryKey: ['notifications'] }),
      'notification:count': () => invalidate({ queryKey: ['notifications'] }),
      'settings:updated': () => invalidate({ queryKey: ['settings'] }),
      'callers:updated': () => invalidate({ queryKey: ['callers'] }),
      'caller:assignments_updated': () => {
        invalidate({ queryKey: ['caller', 'assignments'] });
        invalidate({ queryKey: ['blockAssignments'] });
      },
      'mp_requests:updated': () => {
        invalidate({ queryKey: ['mp'] });
      },
      'records:updated': () => {
        invalidate({ queryKey: ['records'] });
      },
      'dashboard:updated': () => {
        invalidate({ queryKey: ['dashboard'] });
      },
      'voting:updated': () => {
        invalidate({ queryKey: ['voting'] });
      },
      'turnout:updated': () => {
        invalidate({ queryKey: ['turnout'] });
      },
    };

    Object.entries(handlers).forEach(([event, handler]) => {
      on(event, handler);
    });

    return () => {
      allRooms.forEach((room) => unsubscribe(room));
      Object.keys(handlers).forEach((event) => off(event, handlers[event]));
    };
    // registeredRoomsKey captures registeredRooms content; using Key avoids array reference churn
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    subscribe,
    unsubscribe,
    on,
    off,
    user?.user_id,
    user?.role_slug,
    registeredRoomsKey,
    invalidate,
  ]);
}

/**
 * Call from pages to register rooms for the current view. When the page unmounts,
 * pass [] to clear.
 */
export function useRegisterSocketRooms(rooms) {
  const { registerRooms } = useSocketRooms();
  const list = Array.isArray(rooms) ? rooms : [rooms].filter(Boolean);
  const roomsKey = list.join(',');

  useEffect(() => {
    registerRooms(list);
    return () => registerRooms([]);
    // roomsKey is list.join(','); using key avoids list reference churn on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerRooms, roomsKey]);
}
